import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { STATUS_OPTIONS_DESCRIPTION } from '../../../../constants/status';
import {
  MT_BT_SUBTIPO_ALL,
  QUICK_FILTER,
  TOGGLE_GROUP_SX,
  buildMtBtSubtipoOptions,
  buildPrimaryFilterOptions,
  getActiveFilterChips,
  getNsirSubtipoId,
  getNsirTipoServicoId,
  hasActiveFilters,
  isTipoServicoComSubtipoFilter,
  matchesQuickFilter,
  parseTipoServicoFilterValue,
} from '../drawerQuickFilters';
import { useNsirMapaFilterOptions } from '../useNsirMapaFilterOptions';

const resolveOptionLabel = (options, id, fallback) => {
  if (!id) return fallback || null;
  const match = options.find((option) => String(option.id) === String(id));
  return match?.label || fallback || null;
};

const matchesSearch = (nsir, query, tipoServicoOptions, subtipoOptions) => {
  if (!query) return true;
  const q = query.toLowerCase();
  const tipoServicoLabel = resolveOptionLabel(
    tipoServicoOptions,
    getNsirTipoServicoId(nsir, tipoServicoOptions),
    nsir.tipoServico,
  );
  const subtipoLabel = resolveOptionLabel(
    subtipoOptions,
    getNsirSubtipoId(nsir, subtipoOptions),
    nsir.subtipo,
  );
  const haystack = [
    nsir.codigo,
    nsir.equipamento,
    nsir.tipoServico,
    nsir.subtipo,
    tipoServicoLabel,
    subtipoLabel,
    STATUS_OPTIONS_DESCRIPTION[nsir.status],
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

const NsirMapaDrawer = ({
  onClose,
  nsirs,
  title,
  highlightNsirId,
  onSelectNsir,
}) => {
  const {
    tipoServicoOptions,
    subtipoOptions,
    mtBtTipoServicoId,
    loading: optionsLoading,
  } = useNsirMapaFilterOptions();

  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState(QUICK_FILTER.ALL);
  const [mtBtSubtipo, setMtBtSubtipo] = useState(MT_BT_SUBTIPO_ALL);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const filterContext = useMemo(
    () => ({ mtBtTipoServicoId, tipoServicoOptions, subtipoOptions }),
    [mtBtTipoServicoId, tipoServicoOptions, subtipoOptions],
  );

  const primaryFilterOptions = useMemo(
    () => buildPrimaryFilterOptions(tipoServicoOptions),
    [tipoServicoOptions],
  );

  const activeTipoServicoId = parseTipoServicoFilterValue(quickFilter);
  const showTipoSubtipos = isTipoServicoComSubtipoFilter(quickFilter);

  const tipoSubtipoOptions = useMemo(() => {
    if (!showTipoSubtipos) return [];
    return buildMtBtSubtipoOptions(nsirs, {
      ...filterContext,
      tipoServicoId: activeTipoServicoId,
    });
  }, [nsirs, showTipoSubtipos, activeTipoServicoId, filterContext]);

  const activeTipoServicoLabel = useMemo(() => {
    if (!activeTipoServicoId) return 'Inspeção';
    return resolveOptionLabel(tipoServicoOptions, activeTipoServicoId, 'Inspeção');
  }, [activeTipoServicoId, tipoServicoOptions]);

  const activeFilterChips = useMemo(
    () =>
      getActiveFilterChips({
        quickFilter,
        mtBtSubtipo,
        search,
        primaryFilterOptions,
        subtipoOptions,
      }),
    [quickFilter, mtBtSubtipo, search, primaryFilterOptions, subtipoOptions],
  );

  const filtersActive = hasActiveFilters(quickFilter, mtBtSubtipo, search);

  const filteredNsirs = useMemo(() => {
    const trimmed = search.trim();
    return nsirs.filter(
      (nsir) =>
        matchesQuickFilter(nsir, quickFilter, mtBtSubtipo, filterContext) &&
        matchesSearch(nsir, trimmed, tipoServicoOptions, subtipoOptions),
    );
  }, [nsirs, search, quickFilter, mtBtSubtipo, filterContext, tipoServicoOptions, subtipoOptions]);

  const handlePrimaryFilterChange = (_, value) => {
    if (value == null) return;
    setQuickFilter(value);
    setMtBtSubtipo(MT_BT_SUBTIPO_ALL);
  };

  const handleMtBtSubtipoChange = (_, value) => {
    if (value != null) setMtBtSubtipo(value);
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 340 },
        flex: { xs: '1 1 0', md: '0 0 auto' },
        height: { xs: 'auto', md: '100%' },
        maxHeight: { xs: '48%', md: 'none' },
        minHeight: { xs: 200, md: 0 },
        display: 'flex',
        flexDirection: 'column',
        borderLeft: { md: 1 },
        borderTop: { xs: 1, md: 0 },
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, fontSize: '0.875rem' }}
            noWrap
            title={title}
          >
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredNsirs.length.toLocaleString('pt-BR')} de {nsirs.length.toLocaleString('pt-BR')} NSIR(s)
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Fechar lista">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => setFiltersExpanded((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setFiltersExpanded((prev) => !prev);
            }
          }}
          aria-expanded={filtersExpanded}
          aria-label={filtersExpanded ? 'Minimizar filtros' : 'Maximizar filtros'}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Filtros
            </Typography>
            {optionsLoading && <CircularProgress size={12} />}
            {!filtersExpanded && filtersActive && (
              <Chip
                label={activeFilterChips.length}
                size="small"
                color="primary"
                sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
          </Stack>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setFiltersExpanded((prev) => !prev);
            }}
            aria-label={filtersExpanded ? 'Minimizar filtros' : 'Maximizar filtros'}
          >
            {filtersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Stack>

        {!filtersExpanded && filtersActive && (
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ px: 2, pb: 1 }}>
            {activeFilterChips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                variant="outlined"
                sx={{ height: 22, maxWidth: '100%', fontSize: '0.7rem' }}
              />
            ))}
          </Stack>
        )}

        <Collapse in={filtersExpanded}>
          <Stack spacing={1.25} sx={{ px: 2, pb: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar código, equipamento, subtipo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Filtro rápido
              </Typography>
              <ToggleButtonGroup
                value={quickFilter}
                exclusive
                onChange={handlePrimaryFilterChange}
                size="small"
                disabled={optionsLoading}
                sx={TOGGLE_GROUP_SX}
              >
                {primaryFilterOptions.map((opt) => (
                  <ToggleButton key={opt.value} value={opt.value}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {showTipoSubtipos && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Subtipo — {activeTipoServicoLabel}
                </Typography>
                <ToggleButtonGroup
                  value={mtBtSubtipo}
                  exclusive
                  onChange={handleMtBtSubtipoChange}
                  size="small"
                  disabled={optionsLoading}
                  sx={TOGGLE_GROUP_SX}
                >
                  <ToggleButton value={MT_BT_SUBTIPO_ALL}>
                    Todos
                  </ToggleButton>
                  {tipoSubtipoOptions.map((opt) => (
                    <ToggleButton key={opt.id} value={opt.id}>
                      {opt.label} ({opt.count})
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}
          </Stack>
        </Collapse>
      </Box>

      <List
        dense
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          py: 0,
        }}
      >
        {filteredNsirs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {nsirs.length === 0
                ? 'Nenhuma NSIR nesta área.'
                : 'Nenhum resultado para o filtro aplicado.'}
            </Typography>
          </Box>
        ) : (
          filteredNsirs.map((nsir) => {
            const isActive = String(highlightNsirId) === String(nsir.id);
            const subtipoLabel = resolveOptionLabel(
              subtipoOptions,
              getNsirSubtipoId(nsir, subtipoOptions),
              nsir.subtipo,
            );
            const tipoServicoLabel = resolveOptionLabel(
              tipoServicoOptions,
              getNsirTipoServicoId(nsir, tipoServicoOptions),
              nsir.tipoServico,
            );

            return (
              <ListItemButton
                key={nsir.id}
                selected={isActive}
                onClick={() => onSelectNsir(nsir)}
                sx={{
                  alignItems: 'flex-start',
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: (t) => `${t.palette.primary.main}14`,
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                        {nsir.codigo}
                      </Typography>
                      {subtipoLabel && (
                        <Chip
                          label={subtipoLabel}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ height: 20, maxWidth: '100%', fontSize: '0.7rem' }}
                        />
                      )}
                      {tipoServicoLabel && (
                        <Chip
                          label={tipoServicoLabel}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, maxWidth: '100%', fontSize: '0.7rem' }}
                        />
                      )}
                      {nsir.anomalia && (
                        <Chip label="Anomalia" size="small" color="error" sx={{ height: 20 }} />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Stack component="span" spacing={0.25} sx={{ mt: 0.5 }}>
                      <Typography component="span" variant="caption" color="text.secondary" display="block">
                        {STATUS_OPTIONS_DESCRIPTION[nsir.status] || nsir.status}
                      </Typography>
                      {nsir.equipamento && (
                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                          {nsir.equipamento}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              </ListItemButton>
            );
          })
        )}
      </List>

      {filteredNsirs.length > 0 && (
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Button
            component={Link}
            to={`/nsir/${highlightNsirId || filteredNsirs[0]?.id}`}
            target="_blank"
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<OpenInNewIcon />}
            disabled={!highlightNsirId && !filteredNsirs[0]?.id}
          >
            Abrir NSIR selecionada
          </Button>
        </Box>
      )}
    </Box>
  );
};

NsirMapaDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  nsirs: PropTypes.array.isRequired,
  title: PropTypes.string,
  highlightNsirId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectNsir: PropTypes.func.isRequired,
};

NsirMapaDrawer.defaultProps = {
  title: 'NSIRs na área',
  highlightNsirId: null,
};

export default NsirMapaDrawer;
